import { groupBy, maxBy, sumBy } from 'lodash';
import { type IMapCompetenceIntersectionItem } from '../IMapCompetenceIntersectionItem.ts';

/**
 * Risultato raw del calcolo di competenza primaria.
 * Contiene solo il codice layer vettoriale e l'area.
 * La risoluzione in `idCompetenza` avviene in `savePrimaryCompetence()`
 * (useMapIntersectionManager).
 */
export interface IRawPrimaryCompetence {
  vectorLayerCode: string;
  affectedArea: number;
}

/**
 * True se almeno un item di intersezione competenza ha area rilevante ma
 * nessun codice `vectorLayerCode` associato (proprietà identificativa
 * mancante o vuota sul poligono intersecato).
 *
 * Gli item in ingresso contengono solo intersezioni già filtrate per
 * `area > tolerance`, quindi qui non serve rileggere la tolleranza.
 */
export function hasMissingCompetenceCode(
  items: IMapCompetenceIntersectionItem[],
): boolean {
  return items.some(item => !item.competence);
}

/**
 * Raggruppa gli item per codice competenza e somma le aree.
 */
export function groupAndSumByCompetence(
  items: IMapCompetenceIntersectionItem[],
): { competence: string; affectedArea: number }[] {
  const withCompetence = items.filter(item => item.competence);
  const groups = groupBy(withCompetence, item => item.competence);
  return Object.entries(groups).map(([competence, groupItems]) => ({
    competence,
    affectedArea: sumBy(groupItems, item => item.affectedArea),
  }));
}

/**
 * Calcola la competenza primaria prevalente dal layer primario.
 *
 * `competence` in ogni entry è il valore della proprietà
 * identificativa dalla feature del layer vettoriale
 * (= `Competenza.codiceIdentificativoLayerVettoriale`).
 *
 * Restituisce il dato raw (senza `idCompetenza`).
 * La risoluzione via API avviene in `savePrimaryCompetence()`
 * (useMapIntersectionManager).
 */
export function computePrimaryCompetence(
  intersectionItems: IMapCompetenceIntersectionItem[] | null,
): IRawPrimaryCompetence | null {
  if (!intersectionItems) return null;

  const competenceAreas = groupAndSumByCompetence(intersectionItems);
  if (competenceAreas.length === 0) return null;

  const winner = maxBy(competenceAreas, entry => entry.affectedArea);
  if (!winner?.competence) return null;

  return {
    vectorLayerCode: winner.competence,
    affectedArea: winner.affectedArea,
  };
}
